import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { secondsToMilliseconds } from 'date-fns/secondsToMilliseconds';

import type { ErrorResponse } from '@dte/otw-azure-functions/src/common/interfaces/Error';
import { isNotEmpty } from '@dte/otw-utils-core/src/objects/isEmpty';
import { stringifyJSON } from '@dte/otw-utils-core/src/objects/stringifyJSON';
import { normalizeValue } from '@dte/otw-utils-core/src/strings/normalizeValue';
import { formatISO } from '@dte/otw-utils-dates/src/format/formatISO';
import { safeParseURL } from '@dte/otw-utils-url/src/safeParseURL';

import { getTokenExpiration, getTokenUser } from '../../../contexts/TokenContext/isGoodToken';
import { ClientBuildDate } from '../environmentConfig';
import { logError, logInfo } from '../logging/writeLog';
import { fetchWithRetry } from '../network/fetch/fetchWithRetry';
import { BadSubscriptionKeyError } from './BadSubscriptionKeyError';
import { ForbiddenError } from './ForbiddenError';
import { getResponseBody } from './getResponseBody';
import { UnauthorizedError } from './UnauthorizedError';

function getBodyInit(body: unknown): BodyInit | undefined {
	if (body === undefined) {
		return undefined;
	}

	if (typeof body === 'string') {
		return body;
	}

	return stringifyJSON(body);
}

export function getRequestInit(method = 'get', body: unknown = undefined): RequestInit {
	// Stringify JSON body if needed
	const bodyString = getBodyInit(body);

	const headers = new Headers();
	headers.set('Accept', 'application/json');
	headers.set('x-dte-client-build-date', ClientBuildDate);

	// When we have a body, we assume it's JSON
	if (isNotEmpty(bodyString)) {
		headers.set('Content-Type', 'application/json');
	}

	const requestInit: RequestInit = {
		headers: headers,
		method: method,
		mode: 'cors',
		credentials: 'omit',
		body: bodyString,
		keepalive: false,

		signal: AbortSignal.timeout(secondsToMilliseconds(40)),
	};

	// TODO: trace is mostly deprecated
	// Add trace header for non-prod environments
	// if (!environmentConfig.isProd) {
	// 	requestInit.headers['Ocp-Apim-Trace'] = true;
	// }

	return requestInit;
}

function getAuthenticatedRequestInit(
	subscriptionKey: string,
	token: string,
	method = 'get',
	body: unknown = undefined,
): RequestInit {
	const requestInit = getRequestInit(method, body);

	// Add auth headers
	const headers = new Headers(requestInit.headers);
	headers.set('Ocp-Apim-Subscription-Key', subscriptionKey);
	headers.set('Authorization', `Bearer ${token}`);

	requestInit.headers = headers;

	return requestInit;
}

function checkBadSubscriptionKey(appInsights: ReactPlugin, _response: Response, body: ErrorResponse): void {
	let badSubscriptionKey = false;

	const code = body?.error?.code;

	if (['KEY_INVALID', 'KEY_NOT_FOUND'].includes(code)) {
		badSubscriptionKey = true;
	}

	// TODO: this may also need to look at 403 errors coming from APIM if a key is used but not allowed for this app

	if (badSubscriptionKey) {
		// Should never get here as long as app config is correct
		const message = 'Bad subscription key';
		logError(appInsights, message);
		throw new BadSubscriptionKeyError(code, message);
	}
}

function checkForbidden(appInsights: ReactPlugin, response: Response, body: ErrorResponse, _token: string): void {
	// Not a 401
	if (response.status !== 403) {
		return;
	}

	const code = body?.error?.code;

	alert("Your account doesn't have the correct roles for access to this application");
	logError(appInsights, 'Unauthorized, error code: ', { code: code });

	throw new ForbiddenError('Not Authorized');
}

function checkTokenExpired(appInsights: ReactPlugin, _response: Response, body: ErrorResponse, token: string): void {
	const code = body?.error?.code;

	let message: string;

	// TODO: need to be looking at the refreshOn from the authentication result here

	// We expect to see token expired errors
	if (['TOKEN_EXPIRED'].includes(code)) {
		// TODO: try making a silent attempt to acquire a new token
		const user = getTokenUser(token);
		const expiryDate = getTokenExpiration(token);
		message = `Token error: [ ${code} ], user: [ ${user} ], token expiration: [ ${formatISO(expiryDate)} ]`;
		logInfo(appInsights, message);
	}

	// We shouldn't be seeing these types of errors
	if (['TOKEN_NOT_FOUND', 'TOKEN_INVALID'].includes(code)) {
		const user = getTokenUser(token);
		const expiryDate = getTokenExpiration(token);
		message = `Token error: [ ${code} ], user: [ ${user} ], token expiration: [ ${formatISO(expiryDate)} ]`;
		logError(appInsights, message);
	}

	throw new UnauthorizedError('Invalid token');
}

function checkNotAuthenticated(appInsights: ReactPlugin, response: Response, body: ErrorResponse, token: string): void {
	if (response.status !== 401) {
		return;
	}

	checkTokenExpired(appInsights, response, body, token);

	throw new UnauthorizedError('Need to authenticate');
}

/**
 * Throws an error for all error responses.
 * This function never returns normally.
 */
function handleErrorResponse(appInsights: ReactPlugin, response: Response, body: ErrorResponse, token: string): never {
	// See if there's an issue with the subscription key
	checkBadSubscriptionKey(appInsights, response, body);

	// Check for 401 response
	checkNotAuthenticated(appInsights, response, body, token);

	// User doesn't have access
	checkForbidden(appInsights, response, body, token);

	// TODO: if this is an error we need to detect and handle it better
	const errorMessage = normalizeValue(body?.error?.message);
	if (errorMessage) {
		throw new Error(`Error retrieving data: ${errorMessage}`);
	}
	throw new Error('Error retrieving data');
}

async function handleResponse<T>(appInsights: ReactPlugin, response: Response, token: string): Promise<T | undefined> {
	if (!response) {
		logError(appInsights, 'Error: empty response');
		return undefined;
	}

	const responseBody = await getResponseBody<T>(appInsights, response);
	if (response.ok) {
		return responseBody;
	}

	const errorBody = responseBody as ErrorResponse;
	handleErrorResponse(appInsights, response, errorBody, token);
}

export async function authenticatedFetch<T>(
	appInsights: ReactPlugin,
	subscriptionKey: string,
	getToken: () => Promise<string | undefined>,
	url: string | undefined,
	method = 'get',
	body: unknown = undefined,
): Promise<T | undefined> {
	const validURL = safeParseURL(url);
	if (!validURL) {
		return undefined;
	}

	const token = await getToken();
	if (!token) {
		return undefined;
	}

	const requestInit = getAuthenticatedRequestInit(subscriptionKey, token, method, body);
	const requestInfo: RequestInfo = validURL;

	const fetchResponse = await fetchWithRetry(requestInfo, requestInit);
	return handleResponse<T>(appInsights, fetchResponse, token);
}
